import { AxiosResponse } from 'axios';
import { Modifiers } from './modifiers';
import { ComparisonAssessmentQuery, ProfileQuery } from 'src/graphql/_generated';
export type ThenArgs<T> = T extends Promise<infer U> ? U : T;
export type AxiosArgs<T> = T extends AxiosResponse<infer U> ? U : undefined;
export type ArrayElement<A> = A extends readonly (infer T)[] ? T : never;

export enum QKeys {
  Assessment = 'assessment',
  Incident = 'incident',
  Versions = 'versions',
  Control = 'control',
  RankedControls = 'ranked_controls',
  RankedControlsAssessment = 'ranked_controls_assessment',
  Scenario = 'scenario',
  Audit = 'audit',
  DashboardTimeline = 'dashboard_timeline',
  DashboardTimelineActions = 'dashboard_timeline_actions',
  MyExportJobs = 'my_export_jobs',
}

export enum RiskSources {
  Disgruntled,
  Accidental,
  Ineffective,
  Criminal,
  Coerced,
  Criminals,
  Hacktivists,
  Compromised,
  StateSponsored,
  Competitor,
  Press,
  Researcher,
  Regulator,
}

export enum RiskEvents {
  ServiceUnavailability,
  ServiceCompromise,
  InformationBreach,
  AccessToOurSystemBreach,
  ComplianceFailure,
  RulesChange,
  CriticalVulnerabilityPublished,
  HarmfulSpeech,
  ChildSexualViolentContent,
  Harassment,
  Ransomware,
  Worm,
  Spyware,
  Rootkit,
  Dialler,
  DistributedDenialOfService,
  Sabotage,
  OpenSourceIntelligenceAnalysis,
  NetworkScanning,
  NetworkSniffing,
  Lies,
  Threats,
  Phishing,
  Bribes,
  UnauthorisedAccessToSystemComponent,
  UnauthorisedAccessToInformation,
  UnauthorisedSharingOfInformation,
  UnauthorisedModificationOfInformation,
  UnauthorisedDeletionOfInformation,
  MisappropriationMisuseOfResources,
  FalseRepresentation,
  TheftOfMoney,
  SoftwareExploit,
  SQLInjection,
  CrossSiteScripting,
  FileInclusion,
  ControlSystemBypass,
  UseOfStolenCredentials,
  PasswordBruteForce,
  ProcessFailure,
  AuditFailure,
}

export enum RiskConsequencesLevels {
  Operations,
  Compliance,
  Financial,
  Strategic,
}

export enum RiskConsequences {
  ReducedGrowth,
  BusinessDisruption,
  IneffectiveChange,
  SlowRecovery,
  ReducedAccessToStaffSkills,
  LossOfSuppliers,
  EnvironmentalHarm,
  SafetyFailure,
  SocialHarm,
  MedicalHarm,
  NonCompliance,
  PoorConductIntegrity,
  DamagedRegulatorRelations,
  RegulatoryFines,
  LegalChallenge,
  TheftOfMoney,
  UnplannedCosts,
  IncreasedCostsInefficiency,
  DamagedReputation,
  EmbarrassingReporting,
  DamagedInvestorRelations,
}

export enum IncidentType {
  'Near miss' = 'Near miss',
  'Incident' = 'Incident',
}

export interface Assessment {
  id: string;
  name: string;
  description?: string;
  review_date?: string;
  scenarios_count: number;
  expected_loss: number;
  chart: Array<{ x: number; y: number }>;
  created_at: string;
  updated_at: string;
  risk_tolerance: string;
  variables: Variable[];
  scenarios: Scenario[];
  executive_summary: string;
  started_date: string | null;
  meta?: {
    created_at: string;
    created_by: string;
    created_by_id: number;
    updated_at: string;
    updated_by: string;
    updated_by_id: number;
    edited_at: string;
  };
  legacy_version: boolean;
}

export type AssessmentVersion = NonNullable<ComparisonAssessmentQuery['assessmentA']>['versions'][number];

export enum Strategy {
  Treat = 'Treat',
  Transfer = 'Transfer',
  Tolerate = 'Tolerate',
  Terminate = 'Terminate',
}

export enum ControllStatus {
  NotStarted = 'Not Started',
  InProgress = 'In Progress',
  Live = 'Live',
  Abandoned = 'Abandoned',
  Retired = 'Retired',
  NotApplicable = 'Not Applicable',
}

export interface Scenario {
  id: string;
  assessment_id: string;
  assessment_name: string;
  reference_id: string;
  updated_by_id: number;
  name: string;
  risk_owner: UserOptionType | null;
  risk_narrative: string;
  frequency_times: number;
  frequency_years: number;
  frequency_note: string;
  expected_loss: number;
  upper: number;
  lower: number;
  upper_formula: string;
  lower_formula: string;
  note: string;
  event: RiskEvents;
  source: RiskSources;
  consequence: RiskConsequences;
  chart?: { x: number; y: number }[];
  created_at: string;
  updated_at: string;
  edited_at: string;
  management_note: string | null;
  management_strategy: Strategy | null;
  is_incomplete: number;
  incidents: Incident[];
  risk_identified_date: string | null;
  review_date: string | null;
  implementationPlans: {
    control: Control;
    modifiers: unknown[];
    starting_frequency: number;
    starting_lower: number;
    starting_upper: number;
  }[];
  lower_than_minimum?: string;
  legacy_version: boolean;
  status?: string;
}

export interface ProjectedScenario {
  id: string;
  upper: number;
  lower: number;
  frequency_times: number;
  frequency_years: number;
}

export interface ModScenario extends Omit<Scenario, 'event' | 'source' | 'consequence'> {
  source: string;
  event: string;
  consequence: string;
}

export type GetAuditV2Query = {
  organisation_id?: string;
  workspace_id?: string;
  assessment_id?: string;
  scenario_id?: string;
  control_id?: string;
  incident_id?: string;
  user_id?: number;
  skip?: number;
  dir?: 'desc' | 'asc';
  orderBy?: string;
  version?: string;
  eventOnly?: boolean;
};

export type GetAuditV2RootPath =
  | 'users'
  | 'workspaces'
  | 'assessments'
  | 'scenarios'
  | 'controls'
  | 'incidents'
  | 'risk'
  | 'reports';

export interface AuditAction {
  field_name: string;
  before: string | number | null;
  after: string | number | null;
  url?: string | null;
}
export interface AuditEntity {
  name: string | null;
  id: string | null;
  parent_id?: string | null;
  type:
    | 'user'
    | 'control'
    | 'scenario'
    | 'assessment'
    | 'modifier'
    | 'incident'
    | 'userEvent'
    | 'incident_scenario'
    | 'workspace'
    | 'assessment_comment'
    | 'scenario_comment'
    | 'control_comment'
    | 'incident_comment'
    | 'event_Action'
    | 'incident_action'
    | 'assessment_action'
    | 'scenario_action'
    | 'control_action'
    | 'member';

  status: 'available' | 'deleted';
}

export interface AuditItem {
  action_title: string;
  actions: AuditAction[] | null;
  assessment_id: string | null;
  audit_table_id: number;
  control_id: string | null;
  date_time: string;
  entity: AuditEntity;
  entity_action: AuditEntity | null;
  name: string | null;
  profile_photo_path: string | null;
  scenario_id: string | null;
  user_name: string;
  user_email: string;
  workspace_id: string | null;
  organisation_id: string | null;
  types: string;
}

export interface Variable {
  id: number;
  name: string;
  value: number;
  currency: boolean;
  isInUse: string[];
  workspace_variable: boolean;
  alias: string | null;
  isGlobal: boolean;
}

export interface VariablePost extends Omit<Variable, 'value' | 'id'> {
  id?: number;
  value: number | null;
}

export interface Incident {
  id: string;
  refId: string;
  name: string;
  description: null | string;
  source: null | string;
  date: null | string;
  owner: UserOptionType | null;
  impact: null | string;
  financialImpactUpper: null | number;
  financialImpactLower: null | number;
  type: IncidentType | null;
  severity: null | string;
  scenarios: Scenario[];
  scenariosCount: number;
}

export interface ControlLibraryShort {
  ux_id: string;
  name: string;
  shortname: string | null;
  controlLibraryId: string | null;
  description: string | null;
  strategy: Strategy | null;
  framework_id: number | null;
  framework_ux_id: string | null;
  status: ControllStatus | null;
  user_id: number;
  created_at: string;
  updated_at: string;
  frameworkLibraryName: string | null;
  frameworkLibraryId: string | null;
  frameworkShortname: string | null;
  FrameworkAuthor: string | null;
}

export type VoteValue = -1 | null | 1;

export interface ControlLibraryShortRanked extends ControlLibraryShort {
  final_score: number;
  user_vote: VoteValue;
}

export interface Control {
  id: string;
  ux_id: string;
  name: string;
  shortname: string | null;
  controlId: string | null;
  description: string | null;
  strategy: Strategy | null;
  status: ControllStatus | null;
  expected_active_date?: string | null;
  review_date?: string | null;
  annual_cost: number | null;
  vendor: string | null;
  origin: string;
  actual_active_date?: string | null;
  controlLibrary_Id: string | null;
  controlLibrary_name: string | null;
  frameworkLibraryId: string | null;
  frameworkLibrary_name: string | null;
  frameworkLibrary_shortname: string | null;
  frameworkLibrary_ux_id: string | null;
  created_at: string;
  updated_at: string;
  user_id: number;
  owner: UserOptionType | null;
  modifiers?: Modifiers;
  // Raw modifier rows from the API; converted with convertToModifiers before use.
  recommendedModifiers?: unknown;
  scenarios?: Scenario[];
  source: string | null;
  url_name: string | null;
  // 'LEVEL_0'..'LEVEL_5' maturity keys as delivered by the REST API.
  existing_maturity?: string | null;
  target_maturity?: string | null;
}

export enum Plan {
  Paid = 'Paid',
  Trial = 'Trial',
  ExtendedTrial = 'Extended Trial',
  Partner = 'Partner',
  Demo = 'Demo',
  Internal = 'Internal',
  Idle = 'Idle',
  Closed = 'Closed',
}

export enum WorkspaceType {
  Paid = 'Paid',
  Trial = 'Trial',
  ExtendedTrial = 'Extended Trial',
  Partner = 'Partner',
  Demo = 'Demo',
  Internal = 'Internal',
  Idle = 'Idle',
  Closed = 'Closed',
}

export enum SortType {
  ByCreatedDate = 'meta.created_at',
  ByReviewDate = 'review_date',
  ByExpectedLoss = 'expected_loss',
  ByNumberOfScenarios = 'scenarios_count',
  ByName = 'name',
  UpdatedAt = 'meta.updated_at',
  EditedAt = 'meta.edited_at',
}

export type UserOptionType = NonNullable<
  NonNullable<NonNullable<ProfileQuery['profile']>['activeWorkspace']>['owners']
>[number];

export enum ProjectionVariant {
  base = 'base',
  live = 'live',
  complete = 'complete',
}

// export enum ScenarioStatus {
//   Archived = 'ARCHIVED',
//   Closed = 'CLOSED',
//   Draft = 'DRAFT',
//   Open = 'OPEN',
// }

export interface ActionsConf {
  enabled: boolean;
  completionStatus: 'ALL' | 'COMPLETED' | 'INCOMPLETED';
}
